import React from "react"
import {connect} from "react-redux"
import {bindActionCreators} from "redux"
import PropTypes from 'prop-types'
import moment from '../../../../helpers/moment'

import {loadPoolSummaryAsync} from "../../../../actions/poolDetails"
import {Line} from "react-chartjs-2"
import {showModal} from "../../../../actions/modal"
import {UPDATE_BEST_GUESS} from "../../../../actions/poolAvailability"
import {UPDATE_POOL_THRESHOLDS} from "../../../../actions/poolThresholds"
import {MANUALLY_CHANGE_POOL_STATUS} from "../../../../actions/manuallyChangePoolStatus";
import {
  POOL_STATUS_INACTIVE,
  POOL_STATUS_OFFLINE,
  POOL_STATUS_ONLINE
} from "../../../../constants/poolStatuses"

const RiskLevelBased = 'RiskLevelBased'
const Undefined = 'Undefined'

export class PoolSummary extends React.Component {
  componentDidMount() {
    this.props.loadPoolSummaryAsync({
      id: this.props.id
    })
  }

  getEffectiveStatus() {
    const { currentStatus, offlineEvents = [] } = this.props

    if (currentStatus !== POOL_STATUS_OFFLINE && offlineEvents.length > 0) {
      return POOL_STATUS_OFFLINE
    }

    return currentStatus
  }

  isInactiveStatus() {
    const { poolMode, offlineEvents = [] } = this.props
    const effectiveStatus = this.getEffectiveStatus()

    return effectiveStatus === POOL_STATUS_INACTIVE ||
      (effectiveStatus === POOL_STATUS_OFFLINE && poolMode === Undefined && offlineEvents.length === 0)
  }

  renderUpdateBestGuessButton() {
    if (this.props.poolMode === null || this.props.poolMode === RiskLevelBased) {
      return null
    }

    return <button id="UpdateBestGuess" className="btn btn-primary btn-sm"
      onClick={() => this.props.showModal(UPDATE_BEST_GUESS)}>Update</button>
  }

  renderUpdateThresholdsButton() {
    return <button id="UpdateThresholds" className="btn btn-primary btn-sm"
      onClick={() => this.props.showModal(UPDATE_POOL_THRESHOLDS)}>Update</button>
  }

  renderChangePoolStateButton() {
    const { offlineEvents = [] } = this.props
    const effectiveStatus = this.getEffectiveStatus()

    if (this.isInactiveStatus() || (effectiveStatus === POOL_STATUS_OFFLINE && offlineEvents.length === 0)) {
      return null
    }

    return <button id="ManualPoolStatusChange" className="btn btn-primary btn-sm"
      onClick={() => this.props.showModal(MANUALLY_CHANGE_POOL_STATUS)}>Change pool state</button>
  }

  renderCurrentStatus() {
    const { offlineEvents = [] } = this.props
    const effectiveStatus = this.getEffectiveStatus()

    if (effectiveStatus === POOL_STATUS_ONLINE)
      return effectiveStatus

    if (this.isInactiveStatus()) {
      return `${POOL_STATUS_INACTIVE} (no signal)`
    }

    const hasManuallyOfflineEvent = offlineEvents.some(event => event.eventType === "Manual")
    const hasEtaOfflineEvent = offlineEvents.some(event => event.eventType === "PoolEta")

    let additionalText = ""

    if (hasManuallyOfflineEvent && hasEtaOfflineEvent) additionalText = "(manually and by courier capacity)"
    else if (hasManuallyOfflineEvent) additionalText = "(manually)"
    else if (hasEtaOfflineEvent) additionalText = "(by courier capacity)"
    else additionalText = "(no signals)"

    return `${effectiveStatus} ${additionalText}`
  }

  getStatusTooltip() {
    return "Online: the pool is active and has no current offline reason.\n\n" +
      "Offline: the pool has at least one active offline reason, such as manual, courier capacity, or risk level.\n\n" +
      "Inactive: the pool has not received operational signals yet (Pool mode is Undefined).\n\n" +
      "See 'Offline events' for active offline reasons.\n";
  }

  getStatusClassName() {
    if (this.isInactiveStatus()) {
      return 'alert alert-secondary'
    }

    return this.getEffectiveStatus() === POOL_STATUS_ONLINE ? 'alert alert-success' : 'alert alert-danger'
  }


  render() {
    const isRiskLevelBased = this.props.poolMode === RiskLevelBased

    let graphData = this.props.graphData || []
    if(this.props.riskLevelData !== undefined && this.props.riskLevelData !== null && this.props.riskLevelData.length > 0) {
      graphData = this.props.riskLevelData.map(event => {
        let payload = JSON.parse(event.payload)
        return {
          dateTime: moment(event.createdUtc).format('YYYY-MM-DD HH:mm'),
          minutes: payload.RiskLevel
        }
      })
    }

    let datetimes = graphData.map(a => a.dateTime) || []
    let minutes = graphData.map(a => a.minutes) || []

    const data = {
      labels: datetimes,
      datasets: [
        {
          label: isRiskLevelBased ? "RiskLevel value" : "Best guess value (minutes) Time in UTC",
          data: minutes,
          borderColor: "rgb(75, 192, 192)",
          fill: "false",
          borderWidth: 2
        }
      ]
    }

    const options = {
      scales: {
        yAxes: [
          {
            ticks: {
              suggestedMin: 0,
              suggestedMax: isRiskLevelBased ? 10 : 100
            }
          }
        ]
      }
    }

    let rows = (this.props.offlineEvents || []).map((e, index) => (
      <li key={index}>Type <b>{e.eventType}</b> created by {e.updatedBy} at {e.createdUtc}</li>
    ));

    let offlineEventsRow = (
      <ul>{rows.length === 0 ? <li>No events</li> : rows}</ul>
    );

    const statusText = isRiskLevelBased ? "Inferred status" : "Current status"

    return (
      <div className="row">
        <div className="col-md-3">
          <table className="table">
            <tbody>
            <tr>
              <th scope="row">PoolId</th>
              <td>{this.props.id}</td>
            </tr>
            <tr>
              <th scope="row">Events count</th>
              <td>{this.props.eventCount}</td>
            </tr>
            {!isRiskLevelBased &&
              <tr>
                <th scope="row">Last online</th>
                <td>{this.props.lastOnline}</td>
              </tr>
            }
            {!isRiskLevelBased &&
              <tr>
                <th scope="row">Last offline</th>
                <td>{this.props.lastOffline}</td>
              </tr>
            }
            <tr>
              <th scope="row">
                {statusText}
                <span
                  data-toggle="tooltip"
                  className="icon-questionmark"
                  title={this.getStatusTooltip()}/>
              </th>
              <td
                className={this.getStatusClassName()}>
                <div className="container">
                  <div className="row align-items-center">
                    <div className="col">
                      <span>{this.renderCurrentStatus()}</span>
                    </div>
                    <div className="col-auto">
                      {this.renderChangePoolStateButton()}
                    </div>
                  </div>
                </div>
              </td>
            </tr>
            <tr>
              <th scope="row">Offline events</th>
              <td>{offlineEventsRow}</td>
            </tr>
            {!isRiskLevelBased &&
              <tr>
                <th scope="row">Best guess received</th>
                <td>{this.props.bestGuessReceived}</td>
              </tr>
            }
            {isRiskLevelBased &&
              <tr>
                <th scope="row">Current Risk Level</th>
                <td>{this.props.riskLevel}</td>
              </tr>
            }
            {!isRiskLevelBased &&
              <tr>
                <th scope="row">Best guess</th>
                <td>
                  <div className="container">
                    <div className="row align-items-center">
                      <div className="col">
                        <span>{this.props.bestGuess}</span>
                      </div>
                      <div className="col-auto">
                        {this.renderUpdateBestGuessButton()}
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            }
            {!isRiskLevelBased &&
              <tr>
                <th scope="row">Thresholds</th>
                <td>
                  <div className="container">
                    <div className="row">
                      <div className="col">
                        <span>{this.props.isDefault || this.props.offlineThreshold == null ? 'Default' : `Off: ${this.props.offlineThreshold} mins; On: ${this.props.onlineThreshold} mins`}</span>
                      </div>
                      <div className="col-auto">
                        {this.renderUpdateThresholdsButton()}
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            }
            <tr>
              <th scope="row">Pool mode</th>
              <td><strong>{this.props.poolMode}</strong></td>
            </tr>
            </tbody>
          </table>
        </div>
        <div className="col-md-9">

          <Line data={data} options={options}/>
        </div>
      </div>
    )
  }
}

PoolSummary.propTypes = {
  id: PropTypes.string.isRequired,
  lastOnline: PropTypes.string,
  lastOffline: PropTypes.string,
  currentStatus: PropTypes.string.isRequired,
  eventCount: PropTypes.number,
  bestGuessReceived: PropTypes.string,
  riskLevel: PropTypes.number,
  offlineEvents: PropTypes.arrayOf(PropTypes.object),
  bestGuess: PropTypes.string,
  poolThresholds: PropTypes.string,
  offlineThreshold: PropTypes.number,
  onlineThreshold: PropTypes.number,
  isDefault: PropTypes.bool,
  poolMode: PropTypes.string.isRequired,
  graphData: PropTypes.array,
  riskLevelData: PropTypes.array,
  loadPoolSummaryAsync: PropTypes.func,
  showModal: PropTypes.func.isRequired,
  loadRiskLevelsAsync: PropTypes.func
}

const mapStateToProps = (state, props) => {
  let newState = state.poolDetails.poolSummary
  return {
    ...props,
    eventCount: newState.eventCount,
    lastOnline: newState.lastOnline,
    lastOffline: newState.lastOffline,
    currentStatus: newState.currentStatus,
    bestGuessReceived: newState.bestGuessReceived,
    offlineEvents: state.poolDetails.offlineEvents,
    bestGuess: newState.bestGuess,
    poolThresholds: newState.poolThresholds,
    offlineThreshold: newState.offlineThreshold,
    onlineThreshold: newState.onlineThreshold,
    isDefault: newState.isDefault,
    poolMode: newState.poolMode,
    graphData: newState.graphData,
    riskLevel: newState.riskLevel,
    riskLevelData: newState.riskLevelData
  }
}

const mapDispatchToProps = dispatch =>
  bindActionCreators({loadPoolSummaryAsync, showModal}, dispatch)

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(PoolSummary)



// WEBPACK FOOTER //
// ./src/containers/pools/details/summary/index.js